import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiBox, FiDollarSign, FiCalendar, FiFileText } = FiIcons;

export default function AssetModal({ asset, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Elektronik',
    purchaseDate: '',
    warrantyEnd: '',
    price: '',
    location: '',
    serialNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name || '',
        category: asset.category || 'Elektronik',
        purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
        warrantyEnd: asset.warrantyEnd ? asset.warrantyEnd.split('T')[0] : '',
        price: asset.price || '',
        location: asset.location || '',
        serialNumber: asset.serialNumber || '',
        notes: asset.notes || ''
      });
    }
  }, [asset]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Bitte geben Sie einen Namen ein');
      return;
    }

    // Konvertiere zu camelCase für konsistente Verarbeitung
    const payload = {
      id: asset?.id,
      name: formData.name,
      category: formData.category,
      purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate).toISOString() : null,
      warrantyEnd: formData.warrantyEnd ? new Date(formData.warrantyEnd).toISOString() : null,
      price: formData.price ? parseFloat(formData.price) : null,
      location: formData.location,
      serialNumber: formData.serialNumber,
      notes: formData.notes
    };

    onSave(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {asset ? 'Asset bearbeiten' : 'Neues Asset erfassen'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <div className="relative">
                <SafeIcon icon={FiBox} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="z.B. MacBook Pro"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="Elektronik">Elektronik</option>
                  <option value="Möbel">Möbel</option>
                  <option value="Haushaltsgeräte">Haushaltsgeräte</option>
                  <option value="Werkzeug">Werkzeug</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preis (€)</label>
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kaufdatum</label>
                <input
                  type="date"
                  value={formData.purchaseDate}
                  onChange={e => setFormData({ ...formData, purchaseDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Garantie bis</label>
                <input
                  type="date"
                  value={formData.warrantyEnd}
                  onChange={e => setFormData({ ...formData, warrantyEnd: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seriennummer</label>
              <input
                type="text"
                value={formData.serialNumber}
                onChange={e => setFormData({ ...formData, serialNumber: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
                placeholder="Zustand, Kaufort, etc."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}